import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Guards against leaving an in-progress match without confirmation.
 *
 * Behaviour:
 *   • Captures clicks anywhere on the page on <a> / <button> elements that
 *     navigate away. If we have a queued nav request, hands off to caller.
 *   • Wires window.beforeunload so closing the tab / refreshing prompts.
 *   • Returns a `pending` URL ("/path") + open/cancel/confirm helpers so the
 *     page can render its own modal.
 *
 * This is intentionally lightweight — we don't try to fully reproduce the
 * v6 Blocker API (data-router only). Instead we intercept inside the page by
 * wrapping nav calls via the returned `requestLeave()` helper.
 */
export default function useLeaveGuard({ enabled, onConfirmLeave }) {
  const nav = useNavigate();
  const [pendingPath, setPendingPath] = useState(null);
  const skipOnce = useRef(false);

  // 1. Tab close / refresh prompt
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "Are you sure you want to leave the game?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [enabled]);

  // 2. Browser back/forward — intercept popstate
  useEffect(() => {
    if (!enabled) return undefined;
    // push a sentinel state so popstate fires when user hits back
    window.history.pushState({ __leaveGuard: true }, "");
    const handler = () => {
      if (skipOnce.current) {
        skipOnce.current = false;
        return;
      }
      // re-push so we stay on the page until confirmed
      window.history.pushState({ __leaveGuard: true }, "");
      setPendingPath("__BACK__");
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, [enabled]);

  const requestLeave = useCallback((path) => {
    if (!enabled) {
      nav(path);
      return;
    }
    setPendingPath(path);
  }, [enabled, nav]);

  const cancel = useCallback(() => setPendingPath(null), []);

  const confirm = useCallback(async () => {
    const path = pendingPath;
    setPendingPath(null);
    try { await onConfirmLeave?.(); } catch (e) { /* swallow */ }
    if (path === "__BACK__") {
      skipOnce.current = true;
      window.history.back();
    } else if (path) {
      nav(path);
    }
  }, [pendingPath, nav, onConfirmLeave]);

  return { pendingPath, open: !!pendingPath, requestLeave, cancel, confirm };
}
